
import { useStore } from "vuex";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import Spinner from "@/components/Spinner.vue";
import {
  TopicDTO,
  EndpointStatus
} from "@moderrotech/servers-communication-library/src/types";
import Header from "@/components/Header.vue";
import Copyright from "@/components/Copyright.vue";
import PoweredBy from "@/components/PoweredBy.vue";
import _ from "lodash";
import { isLangRtl } from "@moderrotech/servers-communication-library/src/helpers/lang";
import moment from "moment-timezone";
import Modal, { ModalSize, ModalPosition } from "@/components/Modal.vue";
import { Form, Field, ErrorMessage, FormActions } from "vee-validate";
import { useRoute, useRouter } from "vue-router";
import { legacyTimezones } from "@/helpers/time";
import { useTimestamp, useIdle, useSessionStorage } from "@vueuse/core";
import PhoneInput from "@/components/inputs/PhoneInput.vue";
import { customerInfoStorageKey } from "@/store";

export default {
  components: {
    PhoneInput,
    Copyright,
    Header,
    Spinner,
    Modal,
    Form,
    Field,
    ErrorMessage,
    PoweredBy
  },
  emits: ["idle"],
  setup(props: {}, context: { emit: Function }) {
    const targetTopic = ref<string | null>();

    const store = useStore();
    const searchString = ref(null);

    const config = computed(() => store.state.config.config),
      locale = computed((): string => config.value?.appLocale);

    const schedulePeriods = computed(
        () => store.state.auth.profile?.app?.schedule?.periods
      ),
      scheduleTimezone = computed(() => {
        const tz = store.state.auth.profile?.app?.schedule?.timezone;

        return _.includes(moment.tz.names(), tz)
          ? tz
          : _.findKey(legacyTimezones, z => z === tz);
      });

    const { timestamp } = useTimestamp({ controls: true, interval: 500 });

    const isServiceAvailable = computed(() => {
      if (store.getters.isQueueSuspended) {
        return false;
      }
      if (!scheduleTimezone.value) {
        return false;
      }

      const now = moment(timestamp.value).tz(scheduleTimezone.value),
        day = now
          .day(now.day())
          .locale("en")
          .format("dd"),
        hours = now.hours(),
        minutes = now.minutes();

      return (schedulePeriods.value?.[day]?.from?.hours ?? null) === null ||
        (schedulePeriods.value?.[day]?.from?.minutes ?? null) === null ||
        (schedulePeriods.value?.[day]?.to?.hours ?? null) === null ||
        (schedulePeriods.value?.[day]?.to?.minutes ?? null) === null
        ? false
        : schedulePeriods.value?.[day]?.from?.hours <= hours &&
            (schedulePeriods.value?.[day]?.from?.hours === hours
              ? schedulePeriods.value?.[day]?.from?.minutes <= minutes
              : true) &&
            schedulePeriods.value?.[day]?.to?.hours >= hours &&
            (schedulePeriods.value?.[day]?.to?.hours === hours
              ? schedulePeriods.value?.[day]?.to?.minutes > minutes
              : true);
    });

    let scheduleTimer: number | null = null;

    onMounted(() => {
      scheduleTimer = window.setInterval(
        () => isServiceAvailable.effect.raw(),
        5000
      );
    });
    onUnmounted(() => window.clearInterval(scheduleTimer ?? undefined));

    const isScheduleVisible = computed(
      () =>
        !isServiceAvailable.value || config.value.appSchedulePermanentVisible
    );

    const parseSchedulePeriod = (
      day: string
    ): Record<
      "openHours" | "openMinutes" | "closeHours" | "closeMinutes",
      number
    > | null => {
      if (
        (schedulePeriods.value?.[day]?.from?.hours ?? null) === null ||
        (schedulePeriods.value?.[day]?.from?.minutes ?? null) === null ||
        (schedulePeriods.value?.[day]?.to?.hours ?? null) === null ||
        (schedulePeriods.value?.[day]?.to?.minutes ?? null) === null
      ) {
        return null;
      }

      return {
        openHours: +schedulePeriods.value?.[day].from.hours,
        openMinutes: +schedulePeriods.value?.[day].from.minutes,
        closeHours: +schedulePeriods.value?.[day].to.hours,
        closeMinutes: +schedulePeriods.value?.[day].to.minutes
      };
    };

    const scheduleData = computed(() => {
      if (!scheduleTimezone.value) {
        return null;
      }

      const now = moment().tz(scheduleTimezone.value);

      const firstWeekDay = moment.localeData().firstDayOfWeek();

      let data = _.map(
        _.concat(
          _.filter(_.range(0, 7), d => d >= firstWeekDay),
          _.filter(_.range(0, 7), d => d < firstWeekDay)
        ),
        i => {
          const dayAbbreviation = moment()
            .day(i)
            .locale("en")
            .format("dd");

          const times = parseSchedulePeriod(dayAbbreviation);

          const defaultTimeFormat = "h:mm a";

          const convertTimeFormat = (format: string) => {
            const pythonToJsFormats = {
              "%-I:%M %p": defaultTimeFormat, // 12h
              "%-H:%M": "H:mm" // 24h
            };
            return _.get(pythonToJsFormats, format);
          };

          const timeFormat = config.value.timeFormat
            ? convertTimeFormat(config.value.timeFormat as string)
            : defaultTimeFormat;

          // for japanese should be used AM/PM instead of hieroglyphs
          const isJapanese = locale.value === "ja";

          return _.assign(
            {
              day: _.startCase(
                moment()
                  .day(i)
                  .format("dddd")
              )
            },
            times &&
              !(
                times.openHours === times.closeHours &&
                times.openMinutes === times.closeMinutes
              )
              ? {
                  times: `${moment(
                    `${times.openHours}:${times.openMinutes}`,
                    "H:m"
                  )
                    .locale(isJapanese ? "en" : locale.value)
                    .format(timeFormat)} -
            ${
              timeFormat === "H:mm" && +times.closeHours! === 24
                ? "24:00"
                : moment(`${times.closeHours}:${times.closeMinutes}`, "H:m")
                    .locale(isJapanese ? "en" : locale.value)
                    .format(timeFormat)
            }${
                    (config.value.appScheduleDisplayTimezone ?? true) &&
                    moment.tz.zone(scheduleTimezone.value)?.abbr(+now)
                      ? ` (${moment.tz
                          .zone(scheduleTimezone.value)
                          ?.abbr(+now)})`
                      : ""
                  }`
                }
              : {}
          );
        }
      );

      if (!config.value.minimizeSchedule) {
        return data;
      }

      data = _.filter(data, d => !!d.times); // filter not working days

      const minimizedData = [];

      let i = 0;
      while (i < data.length) {
        const equalRows = [data[i]];
        i++;

        for (let j = i; j < data.length; j++) {
          if (data[j].times === equalRows[0].times) {
            equalRows.push(data[j]);
            i++;
          } else {
            break;
          }
        }

        if (equalRows.length === 1) {
          minimizedData.push(equalRows[0]);
        } else if (equalRows.length === 2) {
          minimizedData.push({
            day: `${equalRows[0].day}, ${equalRows[1].day}`,
            times: equalRows[0].times
          });
        } else {
          minimizedData.push({
            day: `${equalRows[0].day} - ${_.last(equalRows)!.day}`,
            times: equalRows[0].times
          });
        }
      }

      return minimizedData;
    });

    const kioskId = store.state.auth.kioskId;

    kioskId
      ? store.commit("metadata/UNSET_CUSTOMER_INFO")
      : store.commit(
          "metadata/SET_CUSTOMER_INFO",
          useSessionStorage(customerInfoStorageKey, {})
        );

    const customerInfo = computed(() => store.state.metadata.customerInfo);

    const router = useRouter(),
      route = useRoute();

    const handleTopicChosen = async (id: string) =>
      await router.replace({
        name: "app",
        params: route.params,
        query: _.assign({ topic: id }, route.query)
      });

    const endpointStatusCode = computed(() => store.getters.endpointStatusCode);

    const handleCustomerInfoSubmit = (
      values: { [k: string]: string },
      actions: FormActions<any>
    ) => {
      actions.resetForm();
      store.commit("metadata/SET_CUSTOMER_INFO", values);

      handleTopicChosen(targetTopic.value!);
    };

    const { idle } = useIdle(2 * 60 * 1000); // 2 min

    watch(
      () => idle.value,
      v => {
        if (v) {
          context.emit("idle");
        }
      },
      { immediate: true }
    );

    const getTopicTitle = (topic: Pick<TopicDTO, "title">, lang?: string) =>
      store.getters["topics/getTitle"]?.(topic, lang);

    return {
      searchString,
      // do not show topics without assigned experts or without title translation
      topics: computed((): Record<string, TopicDTO> | null =>
        store.state.topics.list
          ? _.fromPairs(
              _.map(
                _.sortBy(
                  _.map(
                    _.omitBy(
                      store.state.topics.list,
                      t =>
                        getTopicTitle(t, locale.value) === null ||
                        !_.find(
                          t?.kiosks ?? [],
                          id =>
                            _.trimEnd(_.trimStart(id, "{"), "}") ===
                            store.getters["auth/clientId"]
                        )
                    ),
                    (t, id) =>
                      _.assign(
                        { id },
                        t,
                        _.omitBy(
                          {
                            order: store.getters["auth/property"]?.(
                              "topics.order"
                            )?.[id]
                          },
                          _.isNil
                        )
                      )
                  ),
                  "order"
                ),
                t => [t.id, _.omit(t, "id")]
              )
            )
          : null
      ),
      config: computed(() => store.state.config.config),
      isRtl: computed(() => isLangRtl(locale.value)),
      isServiceAvailable,
      scheduleData,
      isScheduleVisible,
      getTopicTitle,
      handleTopicChosen,
      endpointStatusCode,
      EndpointStatus,
      targetTopic,
      ModalPosition,
      ModalSize,
      handleCustomerInfoSubmit,
      customerInfo,
      locale,
      isKioskType: computed(() => store.getters["auth/isKioskType"]),
      clientId: computed(() => store.getters["auth/clientId"]),
      ..._.pick(_, [
        "values",
        "assign",
        "pick",
        "startsWith",
        "find",
        "lowerCase",
        "keys",
        "includes"
      ])
    };
  }
};
